import React, { useState, useEffect, useRef } from "react";

interface ImageLoaderProps {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  variant?: "default" | "hero";
  onLoad?: () => void; // Added the onLoad callback prop
}

const ImageLoader: React.FC<ImageLoaderProps> = ({
  src,
  alt,
  className,
  priority = false,
  width,
  height,
  style,
  variant = "default",
  onLoad,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Keep a stable reference to the onLoad callback so that changes to the
  // inline function passed from the parent do **not** retrigger the expensive
  // image‑loading effect.
  const onLoadRef = useRef(onLoad);

  // Update the ref whenever the parent supplies a new callback *without*
  // causing this component's heavy effect to rerun.
  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  useEffect(() => {
    // Prevent unnecessary state updates by tracking component mounted state
    let isMounted = true;

    setIsLoaded(false);
    setHasError(false);

    if (!priority) {
      // Use a ref to track the current image instance
      const img = new Image();

      const handleLoad = () => {
        if (isMounted) {
          setIsLoaded(true);
          if (onLoadRef.current) {
            // Defer callback to next tick to avoid React state update conflicts
            setTimeout(() => {
              if (isMounted && onLoadRef.current) onLoadRef.current();
            }, 0);
          }
        }
      };

      const handleError = () => {
        if (isMounted) {
          setHasError(true);
          console.error(`Failed to load image: ${src}`);
        }
      };

      // Set listeners then src to ensure they're registered before loading starts
      img.onload = handleLoad;
      img.onerror = handleError;
      img.src = src;

      return () => {
        isMounted = false;
        img.onload = null;
        img.onerror = null;
      };
    } else {
      setIsLoaded(true);
      // If it's a priority image, call onLoad in next tick if provided
      if (onLoadRef.current) {
        setTimeout(() => {
          if (isMounted && onLoadRef.current) onLoadRef.current();
        }, 0);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [src, priority]);

  const displayStyle = priority
    ? { display: "block" }
    : { display: isLoaded ? "block" : "none" };

  // Memoize styles to prevent recreation on every render
  const variantStyles: Record<string, React.CSSProperties> = React.useMemo(
    () => ({
      default: {
        objectFit: "contain",
        width: "auto",
        maxWidth: "100%",
        height: "auto",
        maxHeight: "500px",
        border: "none",
        borderRadius: "0",
        boxShadow: "none",
      },
      hero: {
        objectFit: "contain",
        width: "auto",
        maxWidth: "80%",
        height: "auto",
        maxHeight: "400px",
        border: "0",
        borderWidth: "0",
        borderColor: "transparent",
        borderRadius: "0",
        shapeRendering: "geometricPrecision",
        textRendering: "geometricPrecision",
        imageRendering: "crisp-edges",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        outline: "none",
        boxShadow: "none",
      },
    }),
    []
  );

  return (
    <div className="image-container">
      {!priority && !isLoaded && !hasError && (
        <div className="image-placeholder">
          <svg
            className="h-8 w-8 text-primary/60"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}

      {hasError && (
        <div className="image-placeholder flex flex-col items-center justify-center text-muted-foreground">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mb-2"
          >
            <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
            <circle cx="12" cy="13" r="3" />
          </svg>
          <p className="text-sm">Image could not be loaded</p>
        </div>
      )}

      <img
        src={src}
        alt={alt}
        className={className}
        style={{
          ...displayStyle,
          ...variantStyles[variant],
          ...(style || {}),
        }}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        width={width}
        height={height}
        // Remove redundant onLoad handler here - we already handle loading in the useEffect
      />
    </div>
  );
};

export default ImageLoader;
